// eslint-disable-next-line import/no-extraneous-dependencies, no-restricted-imports
import $ from 'jquery';

import { renderReactRoot } from '@trello/component-wrapper';
import { ApolloProvider } from '@trello/graphql';
import { TrelloIntlProvider } from '@trello/i18n/provider';

import type { List } from 'app/scripts/models/List';
import { DragSort } from 'app/scripts/views/lib/DragSort';
import { boardModelIdToElementState } from 'app/src/boardModelIdToElementState';
import { LazyBoardListView } from 'app/src/components/BoardListView';
import { openListComposer as openListComposerInTheNewStack } from 'app/src/components/ListComposer';
import { isBoardCanvasModernizationEnabled } from 'app/src/isBoardCanvasModernizationEnabled';
import type { ListView } from '../list/ListView';
import type { BoardView } from './BoardView';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const openListComposer = function (this: any) {
  if (isBoardCanvasModernizationEnabled()) {
    openListComposerInTheNewStack({ position: 0 });
  } else {
    this.model.viewState.openListComposer();
  }
};

export const scrollToList = function (this: BoardView, list: List) {
  if (!isBoardCanvasModernizationEnabled()) {
    return this.viewForList(list)?.el.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }

  const listElement = boardModelIdToElementState.value.lists.get(
    list.get('id'),
  );
  if (!listElement) {
    return;
  }

  listElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

export const viewForList = function (
  this: BoardView,
  list: List,
): ListView | undefined {
  return this.listListView.viewForModel(list);
};

export const postListListRender = function (this: BoardView) {
  this.renderFilteringStatus();
  return DragSort.refreshListCardSortable();
};

const renderModernizedLists = function (this: BoardView) {
  // If the corresponding unmount method has already been defined, the lists
  // view may have already been rendered, so first unmount it.
  // We've observed that this method can be called multiple times in rapid
  // succession, notably when creating a board, so this safety mechanism is
  // important for ensuring that this component is always unmounted.
  this.unmountLists?.call(this);

  const mountPoint = $('.js-list-container', this.$el);
  const { unmount } = renderReactRoot(
    <TrelloIntlProvider>
      <ApolloProvider>
        <LazyBoardListView idBoard={this.model.get('id')} />
      </ApolloProvider>
    </TrelloIntlProvider>,
    mountPoint[0],
  );

  this.unmountLists = function (this: BoardView) {
    unmount();
    delete this.unmountLists;
  };

  return this;
};

const renderLegacyLists = function (this: BoardView) {
  this.listListView.$el.toggleClass('js-list-sortable', this.model.editable());
  $('.board-canvas', this.$el).append(this.listListView.el);
  // Render after adding because postListListRender is dumb and wants the
  // #board element to be in the DOM already
  this.listListView.render();
  this.listListView.delegateEvents();
  return this;
};

/**
 * Renders lists on the board view. This method should only be called once, on
 * initial load, with subsequent calls deferred to {@link rerenderLists}, as
 * the modernized board canvas does not need to be rerendered.
 */
export const renderLists = function (this: BoardView) {
  if (isBoardCanvasModernizationEnabled()) {
    return renderModernizedLists.call(this);
  } else {
    return renderLegacyLists.call(this);
  }
};

export const rerenderLists = function (this: BoardView) {
  // The modernized board canvas does not need to be explicitly rerendered.
  if (isBoardCanvasModernizationEnabled()) {
    return this;
  }
  return renderLegacyLists.call(this);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sortCommit = (e: any, ui: any) => ui.item.trigger('movelist', ui);
